import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { login as apiLogin, saveSession } from '@/domains/auth/services/auth.service';
import { getApiErrorMessage } from '@/utils/apiErrors';
import { USER_TYPE_CONFIG, REGISTERABLE_USER_TYPES } from '@/shared/types/users.types';

// Import du nouveau composant générique PhoneInput
import PhoneInput from '@/components/common/PhoneInput';

const QUICK_REGISTER_TYPES = REGISTERABLE_USER_TYPES.map(type => ({
    type,
    label: USER_TYPE_CONFIG[type].label,
    icon: USER_TYPE_CONFIG[type].icon,
    color: USER_TYPE_CONFIG[type].color,
}));

type LoginMode = 'email' | 'phone';

const INPUT_CLASSES = 'w-full px-4 py-3 bg-slate-50/50 border border-slate-200 rounded-xl text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all';

export default function UniversalLoginForm() {
    const navigate = useNavigate();
    const [params] = useSearchParams();

    const [mode, setMode] = useState<LoginMode>('email');
    const [login, setLogin] = useState('');
    const [phone, setPhone] = useState('');
    const [countryCode, setCountry] = useState('+224');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPwd] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMsg, setSuccess] = useState('');

    useEffect(() => {
        if (params.get('registered') === '1') {
            setSuccess('Inscription réussie ! Connectez-vous pour accéder à votre espace.');
        }
    }, [params]);

    // L'identifiant final concatène proprement le code pays et le numéro nettoyé
    const identifier = mode === 'email'
        ? login.trim()
        : `${countryCode}${phone}`;

    const isValid = identifier.length > 0 && password.length >= 1;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!isValid) {
            setError('Veuillez remplir tous les champs.');
            return;
        }
        setError('');
        setLoading(true);

        try {
            const data = await apiLogin(identifier, password);
            const session = saveSession(data);
            navigate(session.dashboardPath);
        } catch (err: unknown) {
            setError(getApiErrorMessage(err, 'Identifiants incorrects. Vérifiez vos informations.'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 text-slate-900 font-sans flex flex-col antialiased">

            {/* ── HEADER PREMIUM ─────────────────────────────────────────── */}
            <header className="bg-white border-b border-slate-100 sticky top-0 z-50 shadow-sm px-4 h-16 flex items-center">
                <div className="max-w-6xl w-full mx-auto flex items-center justify-between">
                    <Link to="/" className="flex items-center space-x-3 group">
                        <div className="w-9 h-9 bg-teal-500 rounded-xl flex items-center justify-center text-slate-950 font-black text-lg shadow-md shadow-teal-500/20 transition-transform group-hover:scale-105">
                            O
                        </div>
                        <div className="flex flex-col">
                            <span className="font-bold text-base tracking-tight text-slate-900 leading-none">ONAPILA</span>
                            <span className="text-[9px] text-teal-600 font-medium tracking-widest uppercase mt-0.5">Livrer easy</span>
                        </div>
                    </Link>

                    <Link to="/" className="text-xs font-semibold text-slate-500 hover:text-slate-900 px-3 py-2 rounded-xl hover:bg-slate-50 transition-all flex items-center gap-1.5">
                        <i className="ri-arrow-left-line text-sm" />
                        Retour au site
                    </Link>
                </div>
            </header>

            {/* ── ZONE DE CONTENU PRINCIPALE ─────────────────────────────────────────── */}
            <div className="flex-1 flex items-center justify-center px-4 py-12 md:py-16">
                <div className="w-full max-w-[440px]">

                    <div className="text-center mb-6">
                        <h1 className="text-2xl font-black tracking-tight text-slate-900">Connexion</h1>
                        <p className="text-xs text-slate-400 mt-1">
                            Accédez à votre espace unique ONAPILA Logistics.
                        </p>
                    </div>

                    {successMsg && (
                        <div className="bg-emerald-50 border border-emerald-100 text-emerald-800 p-3.5 rounded-xl mb-4 text-xs font-medium flex items-center gap-2.5 animate-fadeIn">
                            <i className="ri-checkbox-circle-fill text-emerald-500 text-base" />
                            <span>{successMsg}</span>
                        </div>
                    )}

                    {/* CARD PRINCIPALE */}
                    <div className="bg-white rounded-2xl border border-slate-100 shadow-xl shadow-slate-200/40 p-6 md:p-8">

                        {error && (
                            <div className="bg-rose-50 border border-rose-100 text-rose-800 p-3.5 rounded-xl mb-5 text-xs font-medium flex items-start gap-2.5 animate-fadeIn">
                                <i className="ri-error-warning-fill text-rose-500 text-base shrink-0" />
                                <span className="leading-normal">{error}</span>
                            </div>
                        )}

                        {/* Onglets sélecteurs (Mode mail / tel) */}
                        <div className="flex bg-slate-100/80 rounded-xl p-1 mb-6">
                            <button
                                type="button"
                                onClick={() => { setMode('email'); setError(''); }}
                                className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2 ${mode === 'email'
                                    ? 'bg-white text-slate-900 shadow-sm'
                                    : 'text-slate-500 hover:text-slate-800'
                                    }`}
                            >
                                <i className="ri-mail-line text-sm" />
                                E-mail
                            </button>
                            <button
                                type="button"
                                onClick={() => { setMode('phone'); setError(''); }}
                                className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2 ${mode === 'phone'
                                    ? 'bg-white text-slate-900 shadow-sm'
                                    : 'text-slate-500 hover:text-slate-800'
                                    }`}
                            >
                                <i className="ri-phone-line text-sm" />
                                Téléphone
                            </button>
                        </div>

                        {/* Formulaire natif */}
                        <form onSubmit={handleSubmit} className="space-y-4">

                            {/* Saisie Identifiant Dynamique */}
                            {mode === 'email' ? (
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                                        Adresse e-mail
                                    </label>
                                    <input
                                        type="email"
                                        value={login}
                                        onChange={e => setLogin(e.target.value)}
                                        placeholder="votre.email@onapila.com"
                                        autoComplete="email"
                                        className={INPUT_CLASSES}
                                    />
                                </div>
                            ) : (
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                                        Numéro de téléphone
                                    </label>
                                    {/* Intégration du PhoneInput générique */}
                                    <PhoneInput
                                        countryCode={countryCode}
                                        phone={phone}
                                        onCountryCodeChange={setCountry}
                                        onPhoneChange={setPhone}
                                        placeholder="620 00 00 00"
                                        disabled={loading}
                                        error={!!error}
                                    />
                                </div>
                            )}

                            {/* Saisie Mot de passe */}
                            <div>
                                <div className="flex items-center justify-between mb-1.5">
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">
                                        Mot de passe
                                    </label>
                                    <Link
                                        to="/forgot-password"
                                        className="text-xs font-semibold text-teal-600 hover:text-teal-700 hover:underline"
                                    >
                                        Oublié ?
                                    </Link>
                                </div>
                                <div className="relative">
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={e => setPassword(e.target.value)}
                                        placeholder="••••••••"
                                        autoComplete="current-password"
                                        className={`${INPUT_CLASSES} pr-11`}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => { setShowPwd(v => !v) }}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1 rounded-lg"
                                        tabIndex={-1}
                                    >
                                        <i className={showPassword ? 'ri-eye-off-line text-base' : 'ri-eye-line text-base'} />
                                    </button>
                                </div>
                            </div>

                            {/* Bouton de soumission principal */}
                            <button
                                type="submit"
                                disabled={loading || !isValid}
                                className="w-full mt-2 py-3 bg-teal-500 text-slate-950 rounded-xl font-bold text-xs uppercase tracking-wider hover:bg-teal-400 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md shadow-teal-500/10 focus:ring-4 focus:ring-teal-500/20"
                            >
                                {loading ? (
                                    <><i className="ri-loader-4-line text-base animate-spin" /> Authentification en cours…</>
                                ) : (
                                    <><i className="ri-shield-user-line text-base" /> Se connecter en sécurité</>
                                )}
                            </button>
                        </form>

                        {/* Séparateur */}
                        <div className="flex items-center my-5">
                            <div className="flex-1 border-t border-slate-100" />
                            <span className="px-3 text-[10px] font-bold text-slate-400 tracking-wider">OU CONTINUER AVEC</span>
                            <div className="flex-1 border-t border-slate-100" />
                        </div>

                        {/* Connexions tierces */}
                        <div className="grid grid-cols-2 gap-2">
                            <button
                                type="button"
                                className="flex items-center justify-center gap-2 py-2.5 px-3 border border-slate-200 rounded-xl hover:bg-slate-50 hover:border-slate-300 transition-colors text-xs font-semibold text-slate-700"
                            >
                                <i className="ri-google-fill text-red-500 text-base" />
                                Google
                            </button>
                            <button
                                type="button"
                                className="flex items-center justify-center gap-2 py-2.5 px-3 border border-slate-200 rounded-xl hover:bg-slate-50 hover:border-slate-300 transition-colors text-xs font-semibold text-slate-700"
                            >
                                <i className="ri-facebook-fill text-blue-600 text-base" />
                                Facebook
                            </button>
                        </div>
                    </div>

                    {/* Zone inscription par profil */}
                    <div className="mt-4 bg-white rounded-2xl border border-slate-100 shadow-md p-5">
                        <p className="text-center text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">
                            Pas encore de compte ? Rejoignez-nous :
                        </p>
                        <div className="grid grid-cols-3 gap-2">
                            {QUICK_REGISTER_TYPES.map(qt => (
                                <Link
                                    key={qt.type}
                                    to={`/register/${qt.type}`}
                                    className="flex flex-col items-center justify-center p-2.5 border border-slate-100 bg-slate-50/50 rounded-xl hover:border-slate-200 hover:bg-white transition-all text-center group"
                                >
                                    <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center text-sm mb-1.5 transition-transform group-hover:scale-105 group-hover:bg-slate-900 group-hover:text-white">
                                        <i className={qt.icon} />
                                    </div>
                                    <span className="text-[10px] font-bold text-slate-700 leading-tight">{qt.label}</span>
                                </Link>
                            ))}
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}